import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APP_CONTEXT_KEYS } from '@core/constants/app-context.constants';
import { ApiResponse, IKeyValueContext } from '@core/models/common.model';
import { getContextByContext, getContextValueByKey } from '@core/utils/app-context.utils';
import { LocalStorageService } from './local-storage.service';

interface StoredMasterContext {
  tenantId: string;
  contexts: IKeyValueContext[];
}

@Injectable({ providedIn: 'root' })
export class MasterContextService {
  private static readonly STORAGE_KEY = 'clis.master-context';

  private readonly base = environment.apiBaseUrl;
  private currentTenantId = '';

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly masterContextSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly rolesSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly statusesSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly gendersSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly specializationsSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly degreesSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly appointmentTypesSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly appointmentStatusSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly billingServiceTypesSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly paymentMethodsSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly opdBillPaymentStatusSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly opdBillStatusSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly fileUploadContextSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly fileUploadSubcontextSubject = new BehaviorSubject<IKeyValueContext[]>([]);
  private readonly medicineTypeSubject = new BehaviorSubject<IKeyValueContext[]>([]);

  readonly loading$ = this.loadingSubject.asObservable();
  readonly masterContext$ = this.masterContextSubject.asObservable();
  readonly roles$ = this.rolesSubject.asObservable();
  readonly statuses$ = this.statusesSubject.asObservable();
  readonly genders$ = this.gendersSubject.asObservable();
  readonly specializations$ = this.specializationsSubject.asObservable();
  readonly degrees$ = this.degreesSubject.asObservable();
  readonly appointmentTypes$ = this.appointmentTypesSubject.asObservable();
  readonly appointmentStatus$ = this.appointmentStatusSubject.asObservable();
  readonly billingServiceTypes$ = this.billingServiceTypesSubject.asObservable();
  readonly paymentMethods$ = this.paymentMethodsSubject.asObservable();
  readonly opdBillPaymentStatus$ = this.opdBillPaymentStatusSubject.asObservable();
  readonly opdBillStatus$ = this.opdBillStatusSubject.asObservable();
  readonly fileUploadContext$ = this.fileUploadContextSubject.asObservable();
  readonly fileUploadSubcontext$ = this.fileUploadSubcontextSubject.asObservable();
  readonly medicineType$ = this.medicineTypeSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly localStorage: LocalStorageService,
  ) {
    this.hydrateFromStorage();
  }

  get loading(): boolean {
    return this.loadingSubject.value;
  }

  get roles(): IKeyValueContext[] {
    return this.rolesSubject.value;
  }

  get statuses(): IKeyValueContext[] {
    return this.statusesSubject.value;
  }

  get genders(): IKeyValueContext[] {
    return this.gendersSubject.value;
  }

  get specializations(): IKeyValueContext[] {
    return this.specializationsSubject.value;
  }

  get degrees(): IKeyValueContext[] {
    return this.degreesSubject.value;
  }

  get appointmentTypes(): IKeyValueContext[] {
    return this.appointmentTypesSubject.value;
  }

  get appointmentStatus(): IKeyValueContext[] {
    return this.appointmentStatusSubject.value;
  }

  get billingServiceTypes(): IKeyValueContext[] {
    return this.billingServiceTypesSubject.value;
  }

  get paymentMethods(): IKeyValueContext[] {
    return this.paymentMethodsSubject.value;
  }

  get opdBillPaymentStatus(): IKeyValueContext[] {
    return this.opdBillPaymentStatusSubject.value;
  }

  get opdBillStatus(): IKeyValueContext[] {
    return this.opdBillStatusSubject.value;
  }

  get fileUploadContext(): IKeyValueContext[] {
    return this.fileUploadContextSubject.value;
  }

  get fileUploadSubcontext(): IKeyValueContext[] {
    return this.fileUploadSubcontextSubject.value;
  }

  get medicineType(): IKeyValueContext[] {
    return this.medicineTypeSubject.value;
  }

  get masterContext(): IKeyValueContext[] {
    return this.masterContextSubject.value;
  }

  getValueByKey(source: IKeyValueContext[], key: string | undefined, fallback = '-'): string {
    if (!key) {
      return fallback;
    }

    const resolved = getContextValueByKey(source, key);
    return resolved !== '-' ? resolved : fallback;
  }

  getValuesByContext(context: string): IKeyValueContext[] {
    return getContextByContext(this.masterContext, context);
  }

  getGenderLabel(key: string | undefined, fallback = '-'): string {
    return this.getValueByKey(this.genders, key, fallback);
  }

  getAppointmentTypeLabel(key: string | undefined, fallback = '-'): string {
    return this.getValueByKey(this.appointmentTypes, key, fallback);
  }

  getAppointmentStatusLabel(key: string | undefined, fallback = '-'): string {
    return this.getValueByKey(this.appointmentStatus, key, fallback);
  }

  getBillingServiceTypeLabel(key: string | undefined, fallback = '-'): string {
    return this.getValueByKey(this.billingServiceTypes, key, fallback);
  }

  getPaymentMethodLabel(key: string | undefined, fallback = '-'): string {
    return this.getValueByKey(this.paymentMethods, key, fallback);
  }

  getOpdBillPaymentStatusLabel(key: string | undefined, fallback = '-'): string {
    return this.getValueByKey(this.opdBillPaymentStatus, key, fallback);
  }

  ensureContextsLoaded(tenantId: string): void {
    if (!tenantId) {
      this.clear();
      return;
    }

    if (this.currentTenantId === tenantId && this.masterContext.length > 0) {
      return;
    }

    const cached = this.localStorage.getObject<StoredMasterContext>(MasterContextService.STORAGE_KEY);
    if (cached?.tenantId === tenantId && cached.contexts?.length) {
      this.applyContexts(cached.contexts, tenantId);
      return;
    }

    this.fetchContexts(tenantId);
  }

  fetchContexts(tenantId: string): void {
    if (!tenantId) {
      this.clear();
      return;
    }

    this.loadingSubject.next(true);

    this.http
      .get<ApiResponse<IKeyValueContext[]>>(`${this.base}/AppContext/tenant/${tenantId}`)
      .subscribe({
        next: (response) => {
          this.applyContexts(response.data ?? [], tenantId);
          this.loadingSubject.next(false);
        },
        error: () => {
          this.clear();
          this.loadingSubject.next(false);
        },
      });
  }

  clear(): void {
    this.currentTenantId = '';
    this.masterContextSubject.next([]);
    this.rolesSubject.next([]);
    this.statusesSubject.next([]);
    this.gendersSubject.next([]);
    this.specializationsSubject.next([]);
    this.degreesSubject.next([]);
    this.appointmentTypesSubject.next([]);
    this.appointmentStatusSubject.next([]);
    this.billingServiceTypesSubject.next([]);
    this.paymentMethodsSubject.next([]);
    this.opdBillPaymentStatusSubject.next([]);
    this.opdBillStatusSubject.next([]);
    this.fileUploadContextSubject.next([]);
    this.fileUploadSubcontextSubject.next([]);
    this.medicineTypeSubject.next([]);
    this.loadingSubject.next(false);
    this.localStorage.removeItem(MasterContextService.STORAGE_KEY);
  }

  private filterByContext(list: IKeyValueContext[], context: string, activeOnly = false): IKeyValueContext[] {
    return list.filter((item) => item.context === context && (!activeOnly || item.isActive !== false));
  }

  private hydrateFromStorage(): void {
    const cached = this.localStorage.getObject<StoredMasterContext>(MasterContextService.STORAGE_KEY);
    if (cached?.tenantId && cached.contexts?.length) {
      this.applyContexts(cached.contexts, cached.tenantId);
    }
  }

  private applyContexts(result: IKeyValueContext[], tenantId: string): void {
    this.currentTenantId = tenantId;
    this.masterContextSubject.next(result);
    this.rolesSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.USER_ROLE));
    this.statusesSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.USER_STATUS));
    this.gendersSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.GENDER));
    this.specializationsSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.SPECIALIZATION));
    this.degreesSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.MED_DEGREE));
    this.appointmentTypesSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.APPOINTMENT_TYPE, true));
    this.appointmentStatusSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.APPOINTMENT_STATUS));
    this.billingServiceTypesSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.BILLING_SERVICE_TYPE));
    this.paymentMethodsSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.PAYMENT_METHOD));
    this.opdBillPaymentStatusSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.OPD_BILL_PAYMENT_STATUS));
    this.opdBillStatusSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.OPD_BILL_STATUS));
    this.fileUploadContextSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.UPLOAD_FILE_SERVICE_CONTEXT));
    this.fileUploadSubcontextSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.UPLOAD_FILE_SERVICE_SUBCONTEXT));
    this.medicineTypeSubject.next(this.filterByContext(result, APP_CONTEXT_KEYS.MEDICINE_TYPE));
    this.localStorage.setObject<StoredMasterContext>(MasterContextService.STORAGE_KEY, {
      tenantId,
      contexts: result,
    });
  }
}

